import { Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ServicesService } from 'app/services/services/services.service';
import { AwscontrolService } from 'app/services/aws/awscontrol.service';
import Swal from 'sweetalert2';
import { Service } from 'app/models/services';
import { Atelier } from 'app/models/atelier';

@Component({
  selector: 'app-list-of-ateliers',
  templateUrl: './list-of-ateliers.component.html',
  styleUrls: ['./list-of-ateliers.component.scss']
})
export class ListOfAteliersComponent implements OnInit {
  serviceId: any;
  service: Service = new Service();
  ateliers: Atelier[] = [];
  isLoading = false;
  agencyId: string | null = null;    // ✅ store agency ID for navigation

  constructor(
    private serviceService: ServicesService,
    private awsService: AwscontrolService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private location: Location
  ) {}

  ngOnInit(): void {
    this.serviceId = this.activatedRoute.snapshot.paramMap.get('id');
    if (!this.serviceId) {
      Swal.fire('Erreur', 'ID du service manquant', 'error');
      this.location.back();
      return;
    }
    this.loadService();
  }

  loadService(): void {
    this.isLoading = true;
    this.serviceService.getService(this.serviceId).subscribe({
      next: (data: any) => {
        this.service = data;
        this.ateliers = data.ateliers || [];

        // ✅ Extract agency ID from service data
        if (data.user?.id) {
          this.agencyId = data.user.id.toString();
        } else if (data.userId) {
          this.agencyId = data.userId.toString();
        } else if (data.agencyId) {
          this.agencyId = data.agencyId.toString();
        } else {
          // Fallback: try to get from the service's agencyName?
          console.warn('Agency ID not found in service data');
        }

        this.isLoading = false;
      },
      error: (err) => {
        console.error(err);
        this.isLoading = false;
        Swal.fire('Erreur', 'Impossible de charger le service', 'error');
      }
    });
  }

  // ---- Suppression d'un atelier (avec suppression de toutes les images) ----
  deleteAtelier(atelier: Atelier, index: number): void {
    Swal.fire({
      title: 'Êtes-vous sûr(e) ?',
      text: 'Vous ne pourrez pas revenir en arrière !',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Oui, supprimez-le.'
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: 'Suppression en cours...',
          allowOutsideClick: false,
          showConfirmButton: false,
          willOpen: () => Swal.showLoading()
        });

        // 1. Supprimer TOUTES les images (principale + galerie)
        this.deleteImagesFromS3(atelier)
          .then(() => {
            // 2. Retirer l'atelier du tableau local
            this.ateliers.splice(index, 1);
            // 3. Mettre à jour le service avec la nouvelle liste
            const updatedService = { ...this.service, ateliers: this.ateliers };
            return this.serviceService.updateService(this.serviceId, updatedService).toPromise();
          })
          .then(() => {
            Swal.close();
            Swal.fire('Supprimé !', 'L\'atelier et toutes ses images ont été supprimés.', 'success');
            this.isLoading = false;
          })
          .catch((err) => {
            Swal.close();
            console.error(err);
            Swal.fire('Erreur', 'La suppression a échoué.', 'error');
            this.isLoading = false;
          });
      }
    });
  }

  /**
   * Supprime toutes les images associées à un atelier (principale + galerie)
   */
  private async deleteImagesFromS3(atelier: Atelier): Promise<void> {
    const imageUrls: string[] = [];

    // Image principale
    if (atelier.mainImageUrl && atelier.mainImageUrl.trim() !== '') {
      imageUrls.push(atelier.mainImageUrl);
    }

    // Images de la galerie
    if (atelier.galleryImages && atelier.galleryImages.length > 0) {
      atelier.galleryImages.forEach(url => {
        if (url && url.trim() !== '') {
          imageUrls.push(url);
        }
      });
    }

    // Si aucune image, on termine immédiatement
    if (imageUrls.length === 0) {
      return;
    }

    // Supprimer chaque image en parallèle, en ignorant les échecs individuels
    const deletePromises = imageUrls.map(url =>
      this.awsService.deleteFile(url).toPromise().catch(err => {
        console.warn(`Échec de la suppression de l'image ${url} :`, err);
        return Promise.resolve(); // ne pas bloquer la suppression des autres
      })
    );

    await Promise.all(deletePromises);
  }

  // ---- Navigation (FIXED) ----
  navigateBack(): void {
    // ✅ Use agencyId instead of serviceId
    if (this.agencyId) {
      this.router.navigate(['/list-Services', this.agencyId]);
    } else {
      // Fallback: go to the agency list or home
      this.router.navigate(['/Agences-des-services']);
    }
  }

  goToEditAtelier(atelierId: number): void {
    this.router.navigate(['/edit-atelier', atelierId]);
  }

  goToAddAtelier(): void {
    this.router.navigate(['/ajouter-atelier', this.serviceId]);
  }
}